import React from "react"

import {
  Blur,
  ButtonWrapper,
  CancelButton,
  CancelOperationButton,
  Container,
  HeaderWrapper,
  Item,
  StyledErrorMessage,
  SecondaryActionButton
} from "./Popup.styled"

import PrimaryButton from "./Buttons/PrimaryButton"
import { Tooltip } from "./Tooltip"

import { ReactComponent as ArrowRight } from "images/icons/arrow-right.svg"

const SECONDARY_TOOLTIP_ID = "popup-secondary-action-tooltip"
const OK_TOOLTIP_ID = "popup-ok-action-tooltip"

type Props = {
  children?: React.ReactNode
  type?: "delete" | "default"
  title: string | React.ReactNode
  subtitle?: string
  item?: {
    itemId: string | undefined
    itemName: string
  }
  buttonText?: string
  cancelButtonText?: string
  loading?: boolean
  handleOkClick?: React.MouseEventHandler
  handleCloseClick: React.MouseEventHandler
  blurBackgroundColor?: boolean
  errorMessage?: string
  hideOkButton?: boolean
  disableOkButton?: boolean
  maxWidth?: string
  cancelOperation?: () => void
  cancelOperationLoading?: boolean
  backgroundColor?: string
  secondaryButtonText?: string
  handleSecondaryClick?: React.MouseEventHandler
  secondaryLoading?: boolean
  disableSecondaryButton?: boolean
  secondaryButtonTooltip?: string
  okButtonTooltip?: string
}

const Popup = ({
  children,
  type = "default",
  title,
  subtitle,
  item,
  loading,
  blurBackgroundColor = true,
  buttonText,
  cancelButtonText,
  handleOkClick,
  handleCloseClick,
  errorMessage,
  hideOkButton,
  disableOkButton,
  maxWidth,
  cancelOperation,
  cancelOperationLoading,
  backgroundColor,
  secondaryButtonText,
  handleSecondaryClick,
  secondaryLoading,
  disableSecondaryButton,
  secondaryButtonTooltip,
  okButtonTooltip
}: Props) => (
  <div>
    <Blur onClick={handleCloseClick} $blurBackgroundColor={blurBackgroundColor} />
    <Container $maxWidth={maxWidth ?? "55rem"} $backgroundColor={backgroundColor}>
      {cancelOperation && (
        <HeaderWrapper>
          <CancelOperationButton
            handleClick={() => cancelOperation()}
            loading={cancelOperationLoading}
          >
            Cancel update
          </CancelOperationButton>
        </HeaderWrapper>
      )}
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
      {item && (
        <Item>
          <li>
            <h4>
              <ArrowRight />
              {item.itemName}
            </h4>
            {item?.itemId && <span>{item.itemId}</span>}
          </li>
        </Item>
      )}
      {errorMessage && errorMessage !== "" && <StyledErrorMessage message={errorMessage} />}
      {children}
      {
        <>
          <ButtonWrapper>
            <CancelButton handleClick={handleCloseClick}>
              {cancelButtonText ?? "Cancel"}
            </CancelButton>
            {handleSecondaryClick && secondaryButtonText && (
              <>
                <SecondaryActionButton
                  handleClick={handleSecondaryClick}
                  loading={secondaryLoading}
                  disabled={disableSecondaryButton}
                  data-tooltip-id={secondaryButtonTooltip ? SECONDARY_TOOLTIP_ID : undefined}
                  data-tooltip-content={secondaryButtonTooltip}
                >
                  {secondaryButtonText}
                </SecondaryActionButton>
                {secondaryButtonTooltip && <Tooltip id={SECONDARY_TOOLTIP_ID} />}
              </>
            )}
            {!hideOkButton && (
              <>
                <PrimaryButton
                  loading={loading}
                  handleClick={handleOkClick}
                  mode={type}
                  disabled={disableOkButton}
                  data-tooltip-id={okButtonTooltip ? OK_TOOLTIP_ID : undefined}
                  data-tooltip-content={okButtonTooltip}
                >
                  {buttonText ?? "Yes, delete"}
                </PrimaryButton>
                {okButtonTooltip && <Tooltip id={OK_TOOLTIP_ID} />}
              </>
            )}
          </ButtonWrapper>
        </>
      }
    </Container>
  </div>
)

export default Popup
